<template>
  <v-container class="ma-0 pa-0" fluid>
    <v-toolbar color="accent" flat dark>
      <v-row align="center" dense>
        <v-breadcrumbs class="white-text" dark :items="breadcrumbs">
          <template #item="{ item }">
            <v-breadcrumbs-item :to="item.to" :disabled="item.disabled">
              <span :class="!item.disabled ? 'white--text' : ''">{{
                item.text
              }}</span>
            </v-breadcrumbs-item>
          </template>
        </v-breadcrumbs>
        <v-spacer />
        <v-btn
          rounded
          :class="body"
          class="text-capitalize yellow darken-2 white--text ml-2"
          @click="contractCopyModal = true"
        >
          <v-icon left>mdi-content-copy</v-icon>Copy
        </v-btn>
        <v-btn
          :disabled="readonly"
          rounded
          :class="body"
          class="text-capitalize green white--text ml-2"
          @click="
            isExistingContract
              ? eventbus.$emit('update')
              : eventbus.$emit('create')
          "
        >
          <v-icon left>mdi-checkbox-marked-circle-outline</v-icon>Save
        </v-btn>
      </v-row>
    </v-toolbar>
    <v-tabs
      background-color="card"
      :active-class="'active'"
      hide-slider
      v-model="tab"
    >
      <v-tab
        :ripple="false"
        :disabled="component.disabled"
        :class="label"
        :active-class="'active'"
        v-for="(component, index) in tabs"
        :key="index"
      >
        <v-spacer />
        <v-icon left>{{ component.icon }}</v-icon>
        <span class="mx-6">{{ component.name }}</span>
        <v-spacer />
        <v-badge
          v-if="index === 1 && addedItems.length"
          color="orange"
          :content="addedItems.length ? addedItems.length : ''"
          :value="index === 1"
          inline
        >
        </v-badge>
      </v-tab>
      <v-spacer />
      <span :class="label" class="main--text my-3 mx-6">
        {{
          isExistingContract
            ? `${contract.vendor_name} - ${contract.vendor_number}`
            : `${vendor.vendor_name} - ${vendor.vendor_number}`
        }}
      </span>
      <v-tab-item v-for="component in tabs" :key="component.name">
        <component
          ref="component"
          :vendor="vendor"
          :vendorInfoDisplay="vendorInfoDisplay"
          :contract="contract"
          :getContract="getContract"
          :is="component.content"
          :isExistingContract="isExistingContract"
          @valid="valid = $event"
          :tab="tab"
          :isBuyer="isBuyer"
          :readonly="readonly"
        />
      </v-tab-item>
    </v-tabs>

    <ContractCopy
      :key="componentKey"
      :value.sync="contractCopyModal"
      :contract="contract"
      :getContract="getContract"
    />

    <Overlay />
  </v-container>
</template>

<script>
import defaultContractTypes from '@/assets/data/contract-types.json'
import { table } from '@/mixins/table-config'
import { displayHelpers } from '@/mixins/display-helpers'
import { mapState } from 'vuex'

export default {
  name: 'ContractView',

  components: {
    ContractCopy: () => import('@/components/contract/ContractCopy'),
    ContractDetails: () => import('@/components/contract/ContractDetails'),
    ContractItems: () => import('@/components/contract/ContractItems'),
    ContractComments: () => import('@/components/contract/ContractComments'),
    Overlay: () => import('@/components/shared/Overlay')
  },

  mixins: [table, displayHelpers],

  data: () => ({
    componentKey: 0,
    component: '',
    contractCopyModal: false,
    modal: false,
    tab: 0,
    contract: {},
    vendor: {},
    vendorInfoDisplay: {},
    valid: null
  }),

  computed: {
    ...mapState([
      'addedItems',
      'comments',
      'contractTypes',
      'currentVendor',
      'isBuyer',
      'readonly'
    ]),

    breadcrumbs() {
      return [
        {
          text: 'Contracts',
          exact: true,
          to: { name: 'Contracts' }
        },
        {
          text: this.isExistingContract
            ? this.contract.description
            : 'Create Contract',
          exact: true,
          disabled: true
        }
      ]
    },

    currentStatus() {
      return this.contract.status
    },

    isExistingContract() {
      return (
        !this._.isEmpty(this.$route.query.id) ||
        !this._.isEmpty(this.contract.contract_uuid)
      )
    },

    tabs() {
      return [
        {
          name: 'Details',
          content: 'ContractDetails',
          icon: 'mdi-card-bulleted-outline'
        },
        {
          name: 'Items',
          content: 'ContractItems',
          icon: 'mdi-format-list-bulleted',
          disabled: !this.isExistingContract
        }
        // { name: 'Comments', content: 'ContractComments', icon: 'mdi-comment-text-outline', disabled: !this.isExistingContract },
      ]
    }
  },

  watch: {
    contract: {
      deep: true,
      handler() {
        if (!this._.isEmpty(this.contract.contract_uuid)) {
          localStorage.setItem('contract_uuid', this.contract.contract_uuid)
        }
        if (!this._.isEmpty(this.contract.status)) {
          localStorage.setItem('contract_status', this.contract.status)
        }
      }
    },

    contractCopyModal: {
      immediate: true,
      handler() {
        this.componentKey += 1
      }
    }
  },

  beforeDestroy() {
    localStorage.removeItem('contract_uuid')
    localStorage.removeItem('contract_status')
  },

  created() {
    this.$store.commit('SET_SNACKBAR', {})
    if (this.$route.query.id || this.isExistingContract) {
      this.$store.dispatch('setOverlay', { status: 'show', text: 'Loading...' })
      this.getContract(
        this.$route.query.id
          ? this.$route.query.id
          : localStorage.getItem('contract_uuid')
      )
    } else {
      this.$store.commit('SET_READONLY', false)
      let values = this._.cloneDeep(defaultContractTypes)
      this.$store.commit('SET_CONTRACT_TYPES', values)
      const { vendor } = this.$route.params
      this.vendor = vendor || JSON.parse(localStorage.filter_vendor)
      this.$store.dispatch('setOverlay', { status: 'hide' })
      this.getVendorInfo(this.vendor.vendor_number)
    }
  },

  methods: {
    goToCopiedContract(contractUUID) {
      this.$router.replace({ query: { id: contractUUID } })
    },
    allowedBeginDates: val =>
      ![1, 2, 3, 4, 5, 6].includes(new Date(val).getDay()),

    allowedEndDates: val =>
      ![0, 1, 2, 3, 4, 5].includes(new Date(val).getDay()),

    async getContract(contractUUID) {
      try {
        const { data } = await this.$contracts.getContract(
          contractUUID
            ? contractUUID
            : this.contract.contract_uuid
            ? this.contract.contract_uuid
            : localStorage.contract_uuid
            ? localStorage.contract_uuid
            : ''
        )
        this.contract = data[0]
        const readonly =
          !this.$auth.isBuyer &&
          this.contract.status !== 'draft' &&
          this.contract.status !== 'declined'
        this.$store.commit('SET_READONLY', readonly)
        this.$store.commit('SET_CONTRACT_STATUS', this.contract.status)
        await this.$store.dispatch('getComments', this.contract)
        await this.$store.dispatch('getStoreTypes', this.contract)
        await this.$store.dispatch('getContractTypes', this.contract)
        await this.$store.dispatch('getItems', this.contract)
        await this.$store.dispatch('getStores', this.contract)
        this.eventbus.$emit('getItems')
        this.getVendorInfo(this.contract.vendor_number)
      } catch {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Error Retrieving Contract'
        })
      } finally {
        this.$store.dispatch('setOverlay', { status: 'hide' })
      }
    },

    async getVendorInfo(vendorNumber) {
      const payload = { vendor_numbers: [vendorNumber] }
      try {
        const { data } = await this.$vendors.getVendorInfo(payload)
        this.vendor = data[0]
        this.vendorInfoDisplay = {
          'Vendor Number': data[0].vendor_number,
          'Vendor Name': data[0].vendor_name,
          'Contact Name': data[0].contact_name,
          'Address Line 1': data[0].address_line_1,
          'Address Line 2': data[0].address_line_2,
          City: data[0].city,
          State: data[0].state,
          Zip: data[0].zip,
          'Vendor Phone': data[0].vendor_phone
        }
      } catch {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Error Retrieving Vendor Information'
        })
      }
    }
  }
}
</script>

<style scoped>
.active {
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-bottom: none;
  box-shadow: 0px 2px 10px 3px rgba(120, 120, 120, 0.75);
  background: #fff;
  color: #385f73 !important;
}
</style>
